import React, { useState, useCallback, useMemo, useEffect } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import advanceFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';

import type { WeekDay, RepetitiveSlot, DayId } from '../appointments/appointments.types.ts';
import '@/assets/styles/views/recurring-calendar.scss';
import clsx from 'clsx';
import { DialogModal } from '@/components';
import { useNavigate, useBlocker } from 'react-router';
import { NavigationPath } from '@/router/navigation-path.ts';
import { useApi } from '@/api/useApi.tsx';
import { useMutationProvider, useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { SetArray } from '@/constants/SetArray.ts';
import ReactPlaceholder from 'react-placeholder';
import { toast } from 'react-toastify';
import ExceptionManager from '@/managers/exception-manager.tsx';
import { ExpertRepetitiveSlotUpdateRequest, ExpertRepetitiveSlotUpdateRequestLiveServiceTypeEnum, ManualCalendarSlotUpdateRequestLiveServiceTypeEnum } from '@dopingtech/live_expert_bff_api_ts/api.ts';
import { useBeforeUnload } from 'react-router';
import { Desktop, Mobile } from '@/utility/responsive.ts';
import { AppointmentCreateRequestLiveServiceTypeEnum } from '@dopingtech/live_expert_bff_api_ts';

dayjs.extend(customParseFormat);
dayjs.extend(advanceFormat);
dayjs.extend(timezone);

const DEFAULT_SLOT_TRESHOLD = 30;

const PlannedRecurringCalendar: React.FC = () => {
	const navigate = useNavigate();
	const { CalendarControllerApi, SlotLimitControllerApi } = useApi();

	const createInitialWeekDays = (): WeekDay[] => {
		const days = [
			{ id: 'MONDAY', day: 'Pazartesi', dayShort: 'Pzt', selected: true },
			{ id: 'TUESDAY', day: 'Salı', dayShort: 'Salı', selected: false },
			{ id: 'WEDNESDAY', day: 'Çarşamba', dayShort: 'Çar', selected: false },
			{ id: 'THURSDAY', day: 'Perşembe', dayShort: 'Per', selected: false },
			{ id: 'FRIDAY', day: 'Cuma', dayShort: 'Cum', selected: false },
			{ id: 'SATURDAY', day: 'Cumartesi', dayShort: 'Cmt', selected: false },
			{ id: 'SUNDAY', day: 'Pazar', dayShort: 'Paz', selected: false }
		];

		return days.map(
			day =>
				({
					...day,
					number: 0
				}) as WeekDay
		);
	};

	const [weekDays, setWeekDays] = useState<WeekDay[]>(createInitialWeekDays());
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [selectedSlots, setSelectedSlots] = useState<SetArray<[DayId, number]>>(new SetArray());
	const [deletedSlots, setDeletedSlots] = useState<SetArray<[DayId, number]>>(new SetArray());
	const [selectedDayId, setSelectedDayId] = useState<DayId>('MONDAY');
	const [slotData, setSlotData] = useState<RepetitiveSlot[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isReflectModalOpen, setIsReflectModalOpen] = useState<boolean>(false);

	const {
		data: repetitiveSlots,
		isLoading: LoadingRepetitiveSlots,
		refetch: refetchRepetitiveSlots
	} = useQueryProvider(
		[QUERY_KEYS.getExpertRepetitiveSlots, 'planned', selectedDayId, AppointmentCreateRequestLiveServiceTypeEnum.PlannedLecture],
		() => CalendarControllerApi.getExpertRepetitiveSlots(selectedDayId, AppointmentCreateRequestLiveServiceTypeEnum.PlannedLecture),
		{
			select: ({ data }) => data.data,
			refetchOnWindowFocus: true
		}
	);

	const {
		data: weekCounts,
		isLoading: LoadingWeekCounts,
		refetch: refetchWeekCounts
	} = useQueryProvider(
		[QUERY_KEYS.countExpertRepetitiveSlotDayByDay, 'planned', ManualCalendarSlotUpdateRequestLiveServiceTypeEnum.PlannedLecture],
		() => CalendarControllerApi.countExpertRepetitiveSlotDayByDay(AppointmentCreateRequestLiveServiceTypeEnum.PlannedLecture),
		{
			select: ({ data }) => data.data,
			refetchOnWindowFocus: true
		}
	);

	const { data: slotLimits } = useQueryProvider(
		[QUERY_KEYS.getSlotLimit, 'planned', AppointmentCreateRequestLiveServiceTypeEnum.PlannedLecture],
		() => SlotLimitControllerApi.getSlotLimit(AppointmentCreateRequestLiveServiceTypeEnum.PlannedLecture),
		{
			select: ({ data }) => data.data,
			refetchOnMount: true
		}
	);

	const saveMutation = useMutationProvider((data: ExpertRepetitiveSlotUpdateRequest) => CalendarControllerApi.saveExpertRepetitiveSlots(data), {
		onSuccess: () => {
			toast.success('Takvim başarıyla güncellendi');
		},
		onError: ExceptionManager.errorHandler
	});

	const slotTreshold = useMemo(() => {
		return slotLimits || DEFAULT_SLOT_TRESHOLD;
	}, [slotLimits]);

	useEffect(() => {
		if (weekCounts && !LoadingWeekCounts && !LoadingRepetitiveSlots) {
			const newWeekDays = weekDays.map(weekDay => {
				let { count } = weekCounts.find(weekCount => weekCount.weekDay === weekDay.id) || { count: 0 };
				selectedSlots.toArray().forEach(slot => {
					if (slot[0] === weekDay.id) {
						count = (count || 0) + 1;
					}
				});
				deletedSlots.toArray().forEach(slot => {
					if (slot[0] === weekDay.id) {
						count = (count || 0) - 1;
					}
				});

				return {
					...weekDay,
					number: count as number
				};
			});
			setWeekDays(newWeekDays);
		}
	}, [weekCounts, LoadingWeekCounts, selectedSlots, deletedSlots, LoadingRepetitiveSlots]);

	useEffect(() => {
		if (repetitiveSlots) {
			setSlotData(repetitiveSlots as unknown as RepetitiveSlot[]);
		}
	}, [repetitiveSlots, selectedDayId]);

	const isChanged = useMemo(() => {
		return !!(selectedSlots.size || deletedSlots.size);
	}, [weekDays, selectedSlots, deletedSlots]);

	useBeforeUnload(
		useCallback(
			e => {
				if (isChanged) {
					e.preventDefault();
					setIsModalOpen(true);
					return 'Takvim Kaydedilmedi!';
				}
				return false;
			},
			[isChanged]
		)
	);

	useBlocker(
		useCallback(() => {
			if (isChanged) {
				setIsModalOpen(true);
				return true;
			}
			return false;
		}, [isChanged])
	);

	const handleDaySelect = useCallback((selectedDayId: DayId) => {
		setWeekDays(prevDays =>
			prevDays.map(day => ({
				...day,
				selected: day.id === selectedDayId
			}))
		);
		setSelectedDayId(selectedDayId);
	}, []);

	const handleTimeSlotClick = (dayId: DayId, timeSlot: RepetitiveSlot) => {
		const selectedSlot: [DayId, number] = [dayId, timeSlot.slot.id];
		const selectedDeletedSlot: [DayId, number] = [dayId, timeSlot?.repetitiveSlotId];

		if (timeSlot.repetitiveSlotId) {
			if (!deletedSlots.has(selectedDeletedSlot)) {
				setDeletedSlots(prevSlots => {
					const newSlots = new SetArray(prevSlots.toArray());
					newSlots.add([dayId, timeSlot.repetitiveSlotId]);
					return newSlots;
				});
			} else {
				setDeletedSlots(prevSlots => {
					const newSlots = new SetArray(prevSlots.toArray());
					newSlots.delete([dayId, timeSlot.repetitiveSlotId]);
					return newSlots;
				});
			}
		} else {
			if (!selectedSlots.has(selectedSlot)) {
				setSelectedSlots(prevSlots => {
					const newSlots = new SetArray(prevSlots.toArray());
					newSlots.add([dayId, timeSlot.slot.id]);
					return newSlots;
				});
			} else {
				setSelectedSlots(prevSlots => {
					const newSlots = new SetArray(prevSlots.toArray());
					newSlots.delete([dayId, timeSlot.slot.id]);
					return newSlots;
				});
			}
		}
	};

	const handleBack = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			if (isChanged) {
				setIsModalOpen(true);
			} else {
				navigate(NavigationPath.gotItPlannedAppointments);
			}
		},
		[isChanged]
	);

	const handleSave = useCallback(
		async (reflectToActiveDays: boolean) => {
			try {
				setIsLoading(true);

				const body: ExpertRepetitiveSlotUpdateRequest = {
					slotIdsToAdd: Array.from(selectedSlots).map(slot => slot[1]),
					repetitiveSlotIdsToDelete: Array.from(deletedSlots).map(slot => slot[1]),
					liveServiceType: ExpertRepetitiveSlotUpdateRequestLiveServiceTypeEnum.PlannedLecture,
					reflectToActiveDays
				};

				await saveMutation.mutateAsync(body);

				await refetchWeekCounts();
				await refetchRepetitiveSlots();

				setSelectedSlots(new SetArray());
				setDeletedSlots(new SetArray());

				setIsLoading(false);
			} catch (error) {
				setIsLoading(false);
				ExceptionManager.errorHandler(error);
			}
		},
		[selectedSlots, deletedSlots, weekDays, refetchRepetitiveSlots, saveMutation, refetchWeekCounts]
	);

	const totalSelectedSlots = useMemo(() => {
		return weekDays.reduce((total, day) => total + (day?.number || 0), 0);
	}, [weekDays]);

	return (
		<div className="recurring-calendar">
			<header className="recurring-calendar__header">
				<a
					href="#"
					className="recurring-calendar__header-back"
					onClick={handleBack}>
					<i className="icons-arrow-left" />
					Tekrarlayan Takvim
				</a>
				<div className="recurring-calendar__header-count">
					<Desktop>Haftalık Görüşme Sayısı</Desktop>
					<ReactPlaceholder
						ready={!isLoading}
						type="rect"
						rows={1}
						style={{ width: 42, marginRight: 0 }}
						className="recurring-calendar__header-count-number placeholder">
						<span
							className={clsx('recurring-calendar__header-count-number', {
								red: totalSelectedSlots < slotTreshold
							})}>
							{totalSelectedSlots || 0}
						</span>
					</ReactPlaceholder>
				</div>
			</header>
			<ReactPlaceholder
				ready={!LoadingWeekCounts}
				className="recurring-calendar__weekdays-day"
				style={{ height: 95, width: '100%' }}
				showLoadingAnimation={true}
				type="rect"
				rows={1}>
				<div className="recurring-calendar__weekdays">
					{weekDays.map((day, index) => (
						<div
							key={index}
							className={clsx('recurring-calendar__weekdays-day', {
								'recurring-calendar__weekdays-day--selected': day.selected
							})}
							onClick={() => handleDaySelect(day.id)}>
							<Mobile>{day.dayShort}</Mobile>
							<Desktop>{day.day}</Desktop>

							{day.number ? <div className="recurring-calendar__weekdays-day-number">{day.number}</div> : <i className="icons-calendar-add" />}
						</div>
					))}
				</div>
			</ReactPlaceholder>

			<h2 className="recurring-calendar__title">Randevu Saatlerini Belirle</h2>

			<div className="recurring-calendar__info">
				<span className="recurring-calendar__info-icon">
					<i className="icons-info-circle" />
				</span>
				Sarı zeminli saatler yoğun zamanları göstermektedir. Bu saatler arasına çok slot açmak görüşme sayınızı artıracaktır.
			</div>

			<div className="recurring-calendar__slots">
				{slotData.map((slot, index) => (
					<ReactPlaceholder
						ready={!LoadingRepetitiveSlots}
						style={{ height: 44 }}
						className="recurring-calendar__slots-time"
						key={index}
						type="rect">
						<div
							key={index}
							className={`recurring-calendar__slots-time recurring-calendar__slots-time--${(slot.repetitiveSlotId && !deletedSlots.has([selectedDayId, slot?.repetitiveSlotId])) || selectedSlots.has([selectedDayId, slot.slot.id]) ? 'selected' : slot?.isPopular ? 'highlighted' : 'available'}`}
							onClick={() => handleTimeSlotClick(selectedDayId, slot)}>
							{(slot.repetitiveSlotId && !deletedSlots.has([selectedDayId, slot.repetitiveSlotId])) || selectedSlots.has([selectedDayId, slot.slot.id]) ? (
								<i className="icons-minus-circle" />
							) : (
								<i className="icons-add-circle" />
							)}
							{dayjs(slot.slot.slotStartTime, 'HH:mm').format('HH:mm')}
						</div>
					</ReactPlaceholder>
				))}
			</div>

			<div className="recurring-calendar__footer">
				<span className="recurring-calendar__info-icon">
					<i className="icons-info-circle" />
				</span>
				<span className="recurring-calendar__footer-text">
					Seçeceğiniz saat aralıkları eğitim öğretim yılı sonuna kadar olan tüm haftalarda otomatik olarak açılacaktır.
					<br />
					Gün bazlı yapmak istediğiniz değişiklikleri <span className="recurring-calendar__footer-link">Randevularım</span> sayfasından yapmalısınız.
				</span>
			</div>

			<div className="recurring-calendar__save">
				<button
					className={clsx('recurring-calendar__save-button', {
						disabled: !isChanged || totalSelectedSlots < slotTreshold || saveMutation.isLoading
					})}
					onClick={() => setIsReflectModalOpen(true)}
					disabled={!isChanged || totalSelectedSlots < slotTreshold || saveMutation.isLoading}>
					Değişiklikleri Kaydet
				</button>
			</div>

			<DialogModal
				isOpen={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				variant="info"
				title="Takvim Kaydedilmedi!"
				primaryButtonText="Tamam"
				onPrimaryClick={() => setIsModalOpen(false)}>
				<p>Tekrarlayan takviminizi kaydetmeden görüşmelere başlayamazsınız. Lütfen ilgili düzenlemeleri yapın ve takviminizi kaydedin.</p>
			</DialogModal>

			<DialogModal
				isOpen={isReflectModalOpen}
				onClose={() => setIsReflectModalOpen(false)}
				variant="info"
				title="Değişiklikleri Kaydet"
				primaryButtonText="Evet, Yansıt"
				secondaryButtonText="Hayır"
				onPrimaryClick={() => {
					setIsReflectModalOpen(false);
					handleSave(true);
				}}
				onSecondaryClick={() => {
					setIsReflectModalOpen(false);
					handleSave(false);
				}}>
				<p>
					Bu değişikliği aktif günlere de yansıtmak ister misiniz? (Seçtiğiniz slotlardan aktif gün penceresi içinde zaten randevusu olanlar bu
					değişiklikten etkilenmeyecek, mevcut haliyle korunacaktır.)
				</p>
			</DialogModal>
		</div>
	);
};

export default PlannedRecurringCalendar;
